import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import AllAisleTestRunner from '../business/all-aisle-test-runner';
import humanizeDuration from 'humanize-duration';
import TestDurationPicker, {DEFAULT_TEST_DURATION_IN_MS} from './test-duration-picker';

const REFRESH_INTERVAL_IN_MS = 1 * 60 * 1000; // 1 min

const CLUSTER_BUTTON_STYLE = {
    margin: '1rem'
};
const CLUSTER_BUTTON_PER_ROW = 2;

const DEFAULT_PATTERN_ID = 'green';
const PATTERN_IDS = ['red', 'green', 'blue', 'grey'];
const QUERY_PARAMETERS = {
    patternId: 'patternId'
};

class AllAisleTester extends React.Component {
    constructor(props) {
        super(props);
        //this.validateQueryParameterAndRedirect();
        this.state = {
            isOnByCluster: this.createInitialRunningState(),
            testDuration: DEFAULT_TEST_DURATION_IN_MS,
            timeNow: Date.now(),
            queryMap : {}
        };
        this.resetRunners();
    }

    componentDidMount() {
        this.intervalTimer = setInterval(this.updateTimeNow, REFRESH_INTERVAL_IN_MS);
    }

    componentDidUpdate(prevProps, prevState) {
        this.validateQueryParameterAndRedirect();
        if (prevProps.siteId !== this.props.siteId) {
            this.resetRunners();
            this.setState({isOnByCluster: this.createInitialRunningState()});
        }
    }

    componentWillUnmount() {
        this.resetRunners();
        clearInterval(this.intervalTimer);
    }

    updateTimeNow = () => {
        // To trigger re-render of the buttons to show the updated remaining time of running tests
        if (this.isAnyClusterRunning()) {
            this.setState({timeNow: Date.now()});
        }
    }

    getPatternId(props) {
        return this.state.queryMap[QUERY_PARAMETERS.patternId] ? this.state.queryMap[QUERY_PARAMETERS.patternId] :  DEFAULT_PATTERN_ID;
    }

    createInitialRunningState() {
        const isOnByCluster = {};
        const clusterConfig = this.getClusterConfig();
        for (const {cluster} of clusterConfig.clusters) {
            isOnByCluster[cluster] = false;
        }

        return isOnByCluster;
    }

    resetRunners() {
        for (const cluster in this.runnerByCluster) {
            const runner = this.runnerByCluster[cluster];
            if (runner.isRunning) {
                runner.stop();
            }
        }
        this.runnerByCluster = {};
        // one runner per cluster
        const clusterConfig = this.getClusterConfig();
        for (const {cluster, ledControllers} of clusterConfig.clusters) {
            const portIdsByControllerIp = {};
            for (const {ip, portIds} of ledControllers) {
                portIdsByControllerIp[ip] = portIds;
            }
            this.runnerByCluster[cluster] = new AllAisleTestRunner({
                siteId: this.props.siteId,
                portIdsByControllerIp,
                callbackOnStart: () => this.setRunningStatus(cluster, true),
                callbackOnStop: () => this.setRunningStatus(cluster, false)
            });
        }
    }

    setRunningStatus(cluster, isRunning) {
        const {isOnByCluster} = this.state;
        isOnByCluster[cluster] = isRunning;
        this.setState({isOnByCluster});
    }

    validateQueryParameterAndRedirect() {
        const patternId = this.getPatternId(this.props);
        if (!PATTERN_IDS.includes(patternId)) {
            const map = this.state.queryMap;
            map[QUERY_PARAMETERS.patternId] = DEFAULT_PATTERN_ID;
            this.setState({queryMap: map});
        }
    }

    getClusterConfig() {
        if (this.props.siteConfig.status === 200) {
            return this.props.siteConfig.data.clusterConfig;
        }
        return null;
    }

    isAnyClusterRunning() {
        return Object.values(this.state.isOnByCluster).some(Boolean);
    }

    isAllClustersRunning() {
        return Object.values(this.state.isOnByCluster).every(Boolean);
    }

    handleClusterClick = (event) => {
        const {cluster} = event.target.dataset;
        const runner = this.runnerByCluster[cluster];
        const {testDuration} = this.state;
        const patternId = this.getPatternId(this.props);

        if (runner) {
            if (!runner.isRunning) {
                runner.start(patternId, testDuration);
            } else {
                runner.stop();
            }
        }
    }

    handleAllClustersOn = () => {
        const clusterConfig = this.getClusterConfig();
        const {testDuration} = this.state;
        const patternId = this.getPatternId(this.props);

        for (const {cluster} of clusterConfig.clusters) {
            this.runnerByCluster[cluster].start(patternId, testDuration);
        }
    }

    handleStop = () => {
        for (const cluster in this.runnerByCluster) {
            const runner = this.runnerByCluster[cluster];
            runner.stop();
        }
    }

    handlePatternChange = (event) => {
        const patternId = event.target.value;
        const map = this.state.queryMap;
        map[QUERY_PARAMETERS.patternId] = patternId;
        this.setState({queryMap: map});
    }

    handleTestDurationChange = (testDuration) => {
        this.setState({testDuration});
    }

    generateClusterButtonLabel(cluster) {
        const {isOnByCluster, testDuration} = this.state;
        let buttonLabel = cluster;

        if (isOnByCluster[cluster]) {
            const testEndTime = this.runnerByCluster[cluster].runEndtime;

            // testEndTime is not set until all aisles are started, which can take a few seconds
            // fallback to display the user picked testDuration if testEndTime is not calculated yet
            const remainingTime = testEndTime ? testEndTime - Date.now() : testDuration;

            const humanizedRemainingTime = humanizeDuration(remainingTime, {
                units: ['d', 'h', 'm'],
                round: true
            });
            buttonLabel += ` (${humanizedRemainingTime} remaining)`;
        }

        return buttonLabel;
    }

    renderClusterButton(cluster) {
        const {isOnByCluster} = this.state;
        const variant = isOnByCluster[cluster] ? 'primary' : 'outline-primary';
        const buttonLabel = this.generateClusterButtonLabel(cluster);

        return (
            <Col key={cluster}>
                <Button
                    variant={variant}
                    data-cluster={cluster}
                    onClick={this.handleClusterClick}
                    className='w-75'
                    style={CLUSTER_BUTTON_STYLE}>
                    {buttonLabel}
                </Button>
            </Col>
        );
    }

    renderClusterButtonGrid() {
        const clusterConfig = this.getClusterConfig();
        const buttons = [<Row key='clusters'><Col>Clusters</Col></Row>];
        let columns = [];
        let rowCount = 0;
        for (const {cluster} of clusterConfig.clusters) {
            const button = this.renderClusterButton(cluster);
            columns.push(button);
            if (columns.length >= CLUSTER_BUTTON_PER_ROW) {
                buttons.push(<Row className="justify-content-md-center"
                    key={rowCount}>{columns}</Row>);
                rowCount += 1;
                columns = [];
            }
        }
        if (columns.length > 0) {
            let i = 0;
            while (columns.length < CLUSTER_BUTTON_PER_ROW) {
                columns.push(<Col key={`placeholder-${i}`}><Button
                    variant='link'
                    disabled={true}
                    active={false}
                    className='w-50'
                    style={CLUSTER_BUTTON_STYLE}></Button></Col>);
                i += 1;
            }
            buttons.push(<Row key={rowCount}>{columns}</Row>);
        }
        return buttons;
    }

    renderPatternIdOptions() {
        return PATTERN_IDS.map((patternId) => <option key={patternId}>{patternId}</option>);
    }

        renderConfigSection() {
        return (
            <Row>
                <Col md={{span: 2, offset: 4}}>
                    <Form.Group>
                        <Form.Label>Pattern</Form.Label>
                        <Form.Control as='select'
                            value={this.getPatternId(this.props)}
                            onChange={this.handlePatternChange}
                        >
                            {this.renderPatternIdOptions()}
                        </Form.Control>
                    </Form.Group>
                </Col>
                <Col md={{span: 2}}>
                    <TestDurationPicker
                        testDuration={this.state.testDuration}
                        onChange={this.handleTestDurationChange}
                    />
                </Col>
            </Row>
        );
    }

    renderBulkActionButtons() {
        return (
            <Col>
                <Button className='mr-3'
                    size='lg'
                    variant={this.isAllClustersRunning() ? 'success' : 'outline-success'}
                    onClick={this.handleAllClustersOn}>ALL ON</Button>
                <Button variant='outline-danger'
                    size='lg'
                    onClick={this.handleStop}>ALL OFF</Button>
            </Col>
        );
    }

    render() {
        return (
            <Container>
                <div className="mb-1" style={{marginTop: '6rem'}}>
                    {this.renderConfigSection()}
                </div>
                {this.renderClusterButtonGrid()}
                <Row className="mt-3">
                    {this.renderBulkActionButtons()}
                </Row>
            </Container>
        );
    }
}

export default AllAisleTester;



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/bintester/component/aisle-tester-with-site-config.jsx